import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useHistoryState } from '@/components/hooks/useHistoryState';
import type { IContentHubItem } from '@/components/sections/content-hub/gallery/item';
import type { ITag } from '@/components/sections/content-hub/tag';
import { clearBodyLockScroll } from '@/components/shared/utility/lock-scroll';
import { trackContentHub } from '@/tag-manager';

const Modal = dynamic(() => import('@/components/shared/overlays/modal'), {
  ssr: false,
});

interface ModalContentHub {
  theme?: 'light' | 'dark';
  initialURL?: string;
  initial?: boolean;
  items: IContentHubItem[];
  currentIndex?: number;
  open: boolean;
  onClose: () => void;
  onSlideChange?: (index: number) => void;
}

export const ModalContentHub = memo(
  ({
    theme = 'light',
    initialURL,
    items = [],
    currentIndex = 0,
    initial,
    open,
    onClose,
  }: ModalContentHub) => {
    const { push, state, replace, isBackPressed } = useHistoryState();

    const router = useRouter();
    const [index, setIndex] = useState<number>(currentIndex);

    const targetImage = useMemo(() => {
      return items[index];
    }, [items, index]);

    const onNextImage = useCallback(() => {
      let imageIndex = index + 1;
      imageIndex = imageIndex >= items.length ? items.length - 1 : imageIndex;
      setIndex(imageIndex);
    }, [index, items.length]);

    const onPrevImage = useCallback(() => {
      const imageIndex = index - 1;
      if (imageIndex < 0) return;
      setIndex(imageIndex);
    }, [index]);

    const onCloseModal = () => {
      if (initialURL) {
        initial
          ? router.push(initialURL)
          : replace(initialURL, { shallow: true });
      }
      onClose?.();
      clearBodyLockScroll();
    };

    const updateURL = () => {
      if (!open || window.location?.pathname === targetImage?.url) return;
      push(targetImage.url, { shallow: true, state: { index } });
    };

    const trackingContentHub = (imageTags: ITag[] = []) => {
      if (!open || imageTags?.length === 0) return;

      const tags = imageTags.map((item) => item.url);
      if (tags.length) {
        trackContentHub({
          tags,
        });
      }
    };

    useEffect(() => {
      if (isBackPressed) {
        if (state?.url === initialURL || !state) {
          return onCloseModal();
        }
      }

      if (
        state?.index !== index &&
        state?.index >= 0 &&
        state?.index < items.length
      ) {
        setIndex(state?.index);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, isBackPressed]);

    useEffect(() => {
      if (!targetImage) return;
      updateURL();
      trackingContentHub(targetImage.tag);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetImage]);

    const description = useMemo(() => {
      if (targetImage?.detail) return targetImage?.detail;
      if (targetImage?.description) return targetImage?.description;
      return '';
    }, [targetImage]);

    return (
      <Modal
        theme={theme}
        type="image"
        size="md"
        open={open}
        onClose={onCloseModal}
        items={[targetImage?.image]}
        footer={undefined}
        isShowCaption={!!targetImage?.title && !!description}
        whiteBackgroundOnMobile
        activeItem={currentIndex}
        onNext={index < items.length - 1 ? onNextImage : undefined}
        onPrev={index > 0 ? onPrevImage : undefined}
        detail={{
          title: targetImage?.title ?? '',
          description: description,
          author: targetImage?.author ?? '',
          youtubeId: targetImage?.youtube_id ?? '',
        }}
        css={{
          '.modal-body': {
            '@maxsm': {
              padding: 0,
              justifyContent: 'center',
            },
          },
        }}
      />
    );
  }
);

ModalContentHub.displayName = 'ModalContentHub';
